import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  extractCheckoutRequiredMeta,
  extractPaymentActionRequiredMeta,
  planManagementService,
  type CustomerSubscriptionChangeRequest,
  type CustomerSubscriptionActionResult,
} from '@/services/customer/plan-management.service';
import { customerPlanManagementKeys } from '@/hooks/customer/useCustomerPlanManagement';
import { extractJsonApiErrorMessage } from '@/lib/jsonapi/errors';
import {
  clearScheduledTransition,
  saveScheduledDowngrade,
} from '@/lib/customer/scheduled-transitions';
import type { CustomerPlanManagementResponse, PaymentActionRequiredMeta } from '@/types/customer-plan-management';

const DEFAULT_CHECKOUT_PATH = '/customer/billing/checkout';

interface UpgradeSubscriptionOptions {
  onPaymentActionRequired?: (meta: PaymentActionRequiredMeta) => void;
}

function buildCheckoutLink(
  checkoutPath: string,
  action: 'upgrade' | 'downgrade' | 'cancel' | 'reactivate',
  request?: CustomerSubscriptionChangeRequest,
): string {
  const params = new URLSearchParams({ action });
  if (request?.planId) {
    params.set('plan_id', request.planId);
  }
  if (request?.priceId) {
    params.set('price_id', request.priceId);
  }
  if (request?.cycleId) {
    params.set('billing_cycle_id', request.cycleId);
  }
  return `${checkoutPath}?${params.toString()}`;
}

function useSharedActionHandlers() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const formatDate = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat(i18n.language ?? 'en', { dateStyle: 'medium' }).format(parsed);
  };

  const handleSuccess = async (message: string) => {
    await queryClient.invalidateQueries({ queryKey: customerPlanManagementKeys.all });
    toast.success(message);
  };

  const handleError = (
    error: unknown,
    fallbackMessage: string,
    action: 'upgrade' | 'downgrade' | 'cancel',
    request?: CustomerSubscriptionChangeRequest,
    checkoutPath = DEFAULT_CHECKOUT_PATH,
  ) => {
    const checkoutMeta = extractCheckoutRequiredMeta(error);
    if (checkoutMeta?.required_action === 'complete_checkout') {
      navigate(buildCheckoutLink(checkoutPath, action, request));
      toast.info(t('customer.planManagement.feedback.checkoutRequired'));
      return;
    }

    toast.error(extractJsonApiErrorMessage(error, fallbackMessage));
  };

  return {
    formatDate,
    t,
    handleSuccess,
    handleError,
  };
}

function resolveCurrentSubscriptionId(queryClient: ReturnType<typeof useQueryClient>): string | null {
  const cached = queryClient.getQueryData<CustomerPlanManagementResponse>(customerPlanManagementKeys.detail());
  return cached?.data.currentSubscription?.id ?? null;
}

export function useUpgradeSubscription(checkoutPath = DEFAULT_CHECKOUT_PATH, options: UpgradeSubscriptionOptions = {}) {
  const { handleSuccess, handleError, t } = useSharedActionHandlers();
  const queryClient = useQueryClient();
  const { onPaymentActionRequired } = options;

  return useMutation<
    CustomerSubscriptionActionResult,
    unknown,
    CustomerSubscriptionChangeRequest & { prorate?: boolean }
  >({
    mutationFn: ({ prorate = true, ...request }) => planManagementService.upgrade(request, prorate),
    onSuccess: async () => {
      // Upgrades are immediate; clear any previously scheduled downgrade banner.
      clearScheduledTransition('current');
      const subscriptionId = resolveCurrentSubscriptionId(queryClient);
      if (subscriptionId) {
        clearScheduledTransition(subscriptionId);
      }
      await handleSuccess(t('customer.planManagement.feedback.upgradeSuccess'));
    },
    onError: (error, variables) => {
      const paymentActionMeta = extractPaymentActionRequiredMeta(error);
      if (paymentActionMeta?.client_secret) {
        onPaymentActionRequired?.(paymentActionMeta);
        toast.info(t('customer.planManagement.paymentAction.notice'));
        return;
      }
      handleError(
        error,
        t('customer.planManagement.feedback.upgradeError'),
        'upgrade',
        variables,
        checkoutPath,
      );
    },
  });
}

export function useDowngradeSubscription(checkoutPath = DEFAULT_CHECKOUT_PATH) {
  const { handleSuccess, handleError, t, formatDate } = useSharedActionHandlers();
  const queryClient = useQueryClient();

  return useMutation<
    CustomerSubscriptionActionResult,
    unknown,
    CustomerSubscriptionChangeRequest & { replacePending?: boolean }
  >({
    mutationFn: ({ replacePending = false, ...request }) =>
      planManagementService.downgrade(request, replacePending),
    onSuccess: async (result, variables) => {
      // Persist a banner so the user keeps seeing that a downgrade is scheduled.
      const subscriptionId = resolveCurrentSubscriptionId(queryClient);
      const cached = queryClient.getQueryData<CustomerPlanManagementResponse>(customerPlanManagementKeys.detail());
      const targetPlan = cached?.data.plans.find((plan) => plan.id === variables.planId) ?? null;
      saveScheduledDowngrade(subscriptionId ?? 'current', result.activeUntil, {
        id: variables.planId,
        name: targetPlan?.name ?? null,
      });
      const message = result.activeUntil
        ? t('customer.planManagement.feedback.downgradeScheduledWithDate', {
            date: formatDate(result.activeUntil),
          })
        : t('customer.planManagement.feedback.downgradeScheduled');
      await handleSuccess(message);
    },
    onError: (error, variables) => {
      handleError(
        error,
        t('customer.planManagement.feedback.downgradeError'),
        'downgrade',
        variables,
        checkoutPath,
      );
    },
  });
}

export function useCancelSubscription(checkoutPath = DEFAULT_CHECKOUT_PATH) {
  const { handleSuccess, handleError, t, formatDate } = useSharedActionHandlers();
  const queryClient = useQueryClient();

  return useMutation<CustomerSubscriptionActionResult, unknown, void>({
    mutationFn: () => planManagementService.cancel(),
    onSuccess: async (result) => {
      clearScheduledTransition('current');
      const subscriptionId = resolveCurrentSubscriptionId(queryClient);
      if (subscriptionId) {
        clearScheduledTransition(subscriptionId);
      }
      const message = result.activeUntil
        ? t('customer.planManagement.feedback.cancelScheduledWithDate', {
            date: formatDate(result.activeUntil),
          })
        : t('customer.planManagement.feedback.cancelScheduled');
      await handleSuccess(message);
    },
    onError: (error) => {
      handleError(error, t('customer.planManagement.feedback.cancelError'), 'cancel', undefined, checkoutPath);
    },
  });
}

export function useResumeSubscription() {
  const { handleSuccess, t } = useSharedActionHandlers();
  const queryClient = useQueryClient();

  return useMutation<CustomerSubscriptionActionResult, unknown, void>({
    mutationFn: () => planManagementService.resume(),
    onSuccess: async () => {
      clearScheduledTransition('current');
      const subscriptionId = resolveCurrentSubscriptionId(queryClient);
      if (subscriptionId) {
        clearScheduledTransition(subscriptionId);
      }
      await handleSuccess(t('customer.planManagement.feedback.resumeSuccess'));
    },
    onError: (error) => {
      toast.error(extractJsonApiErrorMessage(error, t('customer.planManagement.feedback.resumeError')));
    },
  });
}
